import { useRef, useState } from 'react';
import { useRevalidator } from 'react-router';
import type { AddedProductCode } from '@auto-oms/shared/types/addedProductCode';
import { api, type ApiError } from '../lib/api';
import { pushToast } from '../lib/toast';

export async function loadAddedProductCodes(): Promise<AddedProductCode[]> {
  const res = await api.get<{ items: AddedProductCode[] }>('/added-product-code');
  return res.items;
}

type EditCell = { id: number; field: keyof AddedProductCode };

const COLUMNS: { key: keyof AddedProductCode; label: string; editable: boolean }[] = [
  { key: 'channel', label: '채널코드', editable: true },
  { key: 'channelName', label: '채널명', editable: true },
  { key: 'oriCode', label: '품번코드', editable: true },
  { key: 'addedPCodeCh', label: '추가상품코드', editable: true },
  { key: 'addedPName', label: '추가상품명', editable: true },
  { key: 'pDeliveryName', label: '발주 상품명', editable: true },
  { key: 'ea', label: '수량', editable: true },
  { key: 'deliveryPrice', label: '배송비', editable: true },
  { key: 'memo', label: '관리메모', editable: true },
];

const NUMERIC_FIELDS: ReadonlySet<keyof AddedProductCode> = new Set(['ea', 'deliveryPrice']);
const NARROW_FIELDS: ReadonlySet<keyof AddedProductCode> = new Set(['ea', 'deliveryPrice']);

interface Props {
  items: AddedProductCode[];
}

export function AddedProductCodeSection({ items: rawItems }: Props) {
  const items = [...rawItems].sort((a, b) =>
    (a.channel ?? '').localeCompare(b.channel ?? '', 'ko'),
  );
  const { revalidate } = useRevalidator();
  const [editing, setEditing] = useState<EditCell | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({
    channel: 'NAVER',
    channelName: '',
    oriCode: '',
    addedPCodeCh: '',
    addedPName: '',
    pDeliveryName: '',
    ea: 1,
    deliveryPrice: 0,
    memo: '',
  });

  async function submitAdd() {
    if (adding) return;
    if (!newRow.addedPCodeCh.trim() || !newRow.addedPName.trim()) {
      pushToast('error', '추가상품코드와 추가상품명은 필수입니다.');
      return;
    }
    setAdding(true);
    try {
      await api.post('/added-product-code', {
        channel: newRow.channel,
        channelName: newRow.channelName.trim() || null,
        oriCode: newRow.oriCode.trim() || null,
        addedPCodeCh: newRow.addedPCodeCh.trim(),
        addedPName: newRow.addedPName.trim(),
        pDeliveryName: newRow.pDeliveryName.trim() || newRow.addedPName.trim(),
        ea: Math.max(1, Number(newRow.ea) || 1),
        deliveryPrice: Number(newRow.deliveryPrice) || 0,
        memo: newRow.memo.trim() || null,
      });
      pushToast('success', `추가상품 ${newRow.addedPCodeCh}을 추가했습니다.`);
      setNewRow({
        channel: 'NAVER',
        channelName: '',
        oriCode: '',
        addedPCodeCh: '',
        addedPName: '',
        pDeliveryName: '',
        ea: 1,
        deliveryPrice: 0,
        memo: '',
      });
      setAddOpen(false);
      revalidate();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `추가 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setAdding(false);
    }
  }

  function startEdit(item: AddedProductCode, field: keyof AddedProductCode) {
    const col = COLUMNS.find((c) => c.key === field);
    if (!col?.editable) return;
    setEditing({ id: item.id, field });
    setDraft(item[field] != null ? String(item[field]) : '');
    setTimeout(() => inputRef.current?.select(), 0);
  }

  async function commitEdit() {
    if (!editing || saving) return;
    const original = items.find((i) => i.id === editing.id);
    const originalVal = original
      ? original[editing.field] != null
        ? String(original[editing.field])
        : ''
      : '';
    if (draft === originalVal) {
      setEditing(null);
      return;
    }
    setSaving(true);
    try {
      let value: string | number | null;
      if (draft === '') {
        value = null;
      } else if (NUMERIC_FIELDS.has(editing.field)) {
        const n = Number(draft);
        if (!Number.isFinite(n) || n < 0) {
          pushToast('error', '0 이상의 숫자만 입력할 수 있습니다.');
          setSaving(false);
          setEditing(null);
          return;
        }
        value = n;
      } else {
        value = draft;
      }
      await api.patch(`/added-product-code/${editing.id}`, { [editing.field]: value });
      pushToast('success', '저장했습니다.');
      revalidate();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
      setEditing(null);
    }
  }

  async function removeRow(item: AddedProductCode) {
    if (!confirm(`${item.addedPCodeCh ?? ''} 행을 삭제할까요?`)) return;
    try {
      await api.delete(`/added-product-code/${item.id}`);
      pushToast('success', '삭제했습니다.');
      revalidate();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `삭제 실패: ${err.message ?? '알 수 없는 오류'}`);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(null);
  }

  return (
    <section>
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-overline">매핑</p>
          <h2 className="mt-1 text-[22px] font-light tracking-[-0.01em] text-ink-950">
            추가상품 매칭 코드
          </h2>
          <p className="mt-1 text-[13px] text-ink-500">
            채널의 추가상품 코드를 별도 발주 정보로 매칭합니다.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary self-start sm:self-auto"
          onClick={() => setAddOpen(true)}
        >
          행 추가
        </button>
      </header>

      {addOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          onClick={() => !adding && setAddOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-[480px] flex-col overflow-y-auto rounded-t-lg bg-paper p-4 shadow-xl sm:rounded-lg sm:p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-base font-medium text-ink-900">추가상품 매칭 코드 추가</h3>
            <p className="mt-1 text-xs text-ink-500">
              주문 수집 시 추가상품을 부모상품과 분리해 매칭하기 위한 코드입니다.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-ink-700">채널 *</label>
                <select
                  value={newRow.channel}
                  onChange={(e) => setNewRow({ ...newRow, channel: e.target.value })}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                >
                  <option value="NAVER">NAVER</option>
                  <option value="COUPANG">COUPANG</option>
                  <option value="COUPANG_R">COUPANG_R</option>
                  <option value="LM">LM</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">채널 표시명</label>
                <input
                  type="text"
                  value={newRow.channelName}
                  onChange={(e) => setNewRow({ ...newRow, channelName: e.target.value })}
                  placeholder="예: 네이버, 쿠팡"
                  maxLength={50}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">품번코드</label>
                <input
                  type="text"
                  value={newRow.oriCode}
                  onChange={(e) => setNewRow({ ...newRow, oriCode: e.target.value })}
                  placeholder="부모 상품 품번 (예: 1101011)"
                  maxLength={20}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">추가상품코드 *</label>
                <input
                  type="text"
                  value={newRow.addedPCodeCh}
                  onChange={(e) => setNewRow({ ...newRow, addedPCodeCh: e.target.value })}
                  maxLength={20}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">추가상품명 *</label>
                <input
                  type="text"
                  value={newRow.addedPName}
                  onChange={(e) => setNewRow({ ...newRow, addedPName: e.target.value })}
                  maxLength={100}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">발주 상품명</label>
                <input
                  type="text"
                  value={newRow.pDeliveryName}
                  onChange={(e) => setNewRow({ ...newRow, pDeliveryName: e.target.value })}
                  placeholder="비워두면 추가상품명과 동일"
                  maxLength={100}
                  className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="w-24">
                  <label className="block text-sm font-medium text-ink-700">수량</label>
                  <input
                    type="number"
                    min={1}
                    value={newRow.ea}
                    onChange={(e) => setNewRow({ ...newRow, ea: Number(e.target.value) })}
                    className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                  />
                </div>
                <div className="w-28">
                  <label className="block text-sm font-medium text-ink-700">배송비</label>
                  <input
                    type="number"
                    min={0}
                    value={newRow.deliveryPrice}
                    onChange={(e) =>
                      setNewRow({ ...newRow, deliveryPrice: Number(e.target.value) })
                    }
                    className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                  />
                </div>
                <div className="min-w-[160px] flex-1">
                  <label className="block text-sm font-medium text-ink-700">관리메모</label>
                  <input
                    type="text"
                    value={newRow.memo}
                    onChange={(e) => setNewRow({ ...newRow, memo: e.target.value })}
                    maxLength={30}
                    className="mt-1 w-full rounded border border-ink-200 px-3 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAddOpen(false)}
                disabled={adding}
              >
                취소
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={submitAdd}
                disabled={adding}
              >
                {adding ? '추가 중…' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-auto rounded-lg border border-ink-200 bg-paper">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600">
            <tr>
              {COLUMNS.map((col) => {
                const narrow = NARROW_FIELDS.has(col.key);
                return (
                  <th
                    key={col.key}
                    className={[
                      'py-2 text-left whitespace-nowrap',
                      narrow ? 'px-2 text-xs w-16' : 'px-3',
                    ].join(' ')}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.editable && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[11px] font-normal text-ink-400"
                          title="수정 가능"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="h-3 w-3"
                            aria-hidden="true"
                          >
                            <path d="M13.586 3.586a2 2 0 1 1 2.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793 3 14.172V17h2.828l8.379-8.379-2.828-2.828z" />
                          </svg>
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
              <th className="px-3 py-2 text-left whitespace-nowrap w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-3 py-8 text-center text-sm text-ink-500"
                >
                  등록된 추가상품 매칭 코드가 없습니다.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="border-t border-ink-100 hover:bg-ink-50">
                {COLUMNS.map((col) => {
                  const isEditing = editing?.id === item.id && editing?.field === col.key;
                  const displayVal = item[col.key] != null ? String(item[col.key]) : '—';
                  const narrow = NARROW_FIELDS.has(col.key);
                  return (
                    <td
                      key={col.key}
                      className={[
                        'py-2',
                        narrow ? 'px-2 text-xs w-16' : 'px-3',
                        col.editable ? 'cursor-pointer hover:bg-coral-50' : '',
                        col.key === 'addedPCodeCh' || col.key === 'oriCode'
                          ? 'font-mono text-xs font-medium text-ink-800'
                          : '',
                      ].join(' ')}
                      onClick={() => col.editable && startEdit(item, col.key)}
                    >
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          className="w-full rounded border border-coral-400 px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-coral-500"
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={handleKeyDown}
                          disabled={saving}
                        />
                      ) : (
                        <span className="whitespace-normal break-words">{displayVal}</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="text-xs text-ink-500 hover:text-coral-600"
                    onClick={() => removeRow(item)}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
